// File: frontend/src/pages/fees/FeeStructure.jsx

import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import PageHeader from '../../components/PageHeader';

const FeeStructure = () => {
  const [structures, setStructures] = useState([]);
  const [categories, setCategories] = useState([]);

  // Filters & Form State
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');
  
  // New Category Modal Form State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');

  // New Structure Modal Form State
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [targetClass, setTargetClass] = useState('1');
  const [targetCategory, setTargetCategory] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetFrequency, setTargetFrequency] = useState('monthly');

  // Edit Structure Modal Form State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editClass, setEditClass] = useState('1');
  const [editCategory, setEditCategory] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editFrequency, setEditFrequency] = useState('monthly');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  /**
   * Fetch fee categories and fee structures from API
   */
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch active fee categories with Authorization header attached via api instance
      const catRes = await api.get('/api/v1/fees/categories');
      if (catRes.data?.success) {
        const catList = catRes.data.data || [];
        setCategories(catList);
        if (catList.length > 0 && !targetCategory) {
          setTargetCategory(catList[0].id);
        }
      }

      // Fetch fee structures catalog
      const structRes = await api.get('/api/v1/fees/structures', {
        params: { class_id: selectedClassFilter }
      });
      if (structRes.data?.success) {
        setStructures(structRes.data.data || []);
      }
    } catch (err) {
      console.error('Error loading fee structure data:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching fee structures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassFilter]);

  /**
   * Add new fee category submit
   */
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;

    try {
      setSubmitting(true);
      const res = await api.post('/api/v1/fees/categories', {
        name: newCatName.trim(),
        description: newCatDesc.trim()
      });

      if (res.data?.success) {
        setSuccessMsg('Fee category created successfully!');
        setNewCatName('');
        setNewCatDesc('');
        setShowCategoryModal(false);
        fetchData();
      }
    } catch (err) {
      console.error('Error adding fee category:', err);
      alert(err.response?.data?.message || 'Failed to add fee category');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Add new fee structure submit
   */
  const handleAddStructure = async (e) => {
    e.preventDefault();
    if (!targetClass || !targetCategory || !targetAmount) {
      alert('All required fields must be filled out');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/api/v1/fees/structures', {
        class_id: targetClass,
        category_id: targetCategory,
        amount: parseFloat(targetAmount),
        frequency: targetFrequency
      });

      if (res.data?.success) {
        setSuccessMsg('Fee structure entry created successfully!');
        setTargetAmount('');
        setShowStructureModal(false);
        fetchData();
      }
    } catch (err) {
      console.error('Error adding fee structure:', err);
      alert(err.response?.data?.message || 'Failed to add fee structure');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Open Edit Structure Modal with populated state
   */
  const handleEditClick = (item) => {
    setEditId(item.id);
    setEditClass(item.class_id || '1');
    setEditCategory(item.category_id || (categories[0]?.id || ''));
    setEditAmount(item.amount || '');
    setEditFrequency(item.frequency || 'monthly');
    setShowEditModal(true);
  };

  /**
   * Submit updated fee structure entry
   */
  const handleUpdateStructure = async (e) => {
    e.preventDefault();
    if (!editClass || !editCategory || !editAmount) {
      alert('All required fields must be filled out');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.put(`/api/v1/fees/structures/${editId}`, {
        class_id: editClass,
        category_id: editCategory,
        amount: parseFloat(editAmount),
        frequency: editFrequency
      });

      if (res.data?.success) {
        setSuccessMsg('Fee structure entry updated successfully!');
        setShowEditModal(false);
        fetchData();
      }
    } catch (err) {
      console.error('Error updating fee structure:', err);
      alert(err.response?.data?.message || 'Failed to update fee structure');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Delete fee structure entry
   */
  const handleDeleteStructure = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fee structure entry?')) return;
    try {
      const res = await api.delete(`/api/v1/fees/structures/${id}`);
      if (res.data?.success) {
        setSuccessMsg('Fee structure entry deleted successfully!');
        fetchData();
      }
    } catch (err) {
      console.error('Error deleting fee structure:', err);
      alert(err.response?.data?.message || 'Failed to delete fee structure');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <PageHeader
          title="Fee Structure Setup"
          subtitle="Configure and manage class-wise fee categories & pricing rates"
          breadcrumbs={[
            { label: 'Fees', path: '/fees' },
            { label: 'Fee Structure' }
          ]}
          backUrl="/fees"
        >
          <button
            onClick={() => setShowCategoryModal(true)}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-xs transition"
          >
            + Add Category
          </button>
          <button
            onClick={() => setShowStructureModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 rounded-xl text-xs transition shadow-sm"
          >
            + Add Structure Entry
          </button>
        </PageHeader>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        
        {/* Class Filter Bar */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-slate-800 text-base">Filter Fee Structures</h2>
            <p className="text-xs text-slate-500">Displaying fee breakdown grouped by class</p>
          </div>

          <select
            value={selectedClassFilter}
            onChange={(e) => setSelectedClassFilter(e.target.value)}
            className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary font-semibold text-slate-800"
          >
            <option value="all">All Classes</option>
            <option value="1">Class 1</option>
            <option value="2">Class 2</option>
            <option value="3">Class 3</option>
            <option value="4">Class 4</option>
            <option value="5">Class 5</option>
            <option value="6">Class 6</option>
            <option value="7">Class 7</option>
            <option value="8">Class 8</option>
            <option value="9">Class 9</option>
            <option value="10">Class 10</option>
            <option value="11">Class 11</option>
            <option value="12">Class 12</option>
          </select>
        </div>

        {/* Status Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800">✕</button>
          </div>
        )}

        {successMsg && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-sm font-semibold flex items-center justify-between shadow-sm">
            <span>{successMsg}</span>
          </div>
        )}

        {/* Fee Structure Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-4">Class</th>
                  <th className="px-6 py-4">Fee Category</th>
                  <th className="px-6 py-4">Frequency</th>
                  <th className="px-6 py-4 text-right">Amount (Rs.)</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading fee structure catalog...</span>
                      </div>
                    </td>
                  </tr>
                ) : structures.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No fee structure entries configured yet. Click "+ Add Structure" to create one.
                    </td>
                  </tr>
                ) : (
                  structures.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/80 transition">
                      <td className="px-6 py-4 font-bold text-slate-900">
                        Class {item.class_id}
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-800">
                        {item.category_name || 'General Tuition'}
                      </td>
                      <td className="px-6 py-4 capitalize text-slate-600">
                        {item.frequency || 'Monthly'}
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-bold text-primary">
                        Rs. {parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                      </td>
                      <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditClick(item)}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-800 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStructure(item.id)}
                          className="text-xs font-semibold text-red-600 hover:text-red-800 px-2.5 py-1 bg-red-50 hover:bg-red-100 rounded-lg transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {/* Modal 1: Add Fee Category */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="font-bold text-lg text-slate-800">Add Fee Category</h3>
              <button onClick={() => setShowCategoryModal(false)} className="font-bold text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Category Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Tuition Fee, Exam Fee, Lab Fee"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Description</label>
                <input
                  type="text"
                  placeholder="Optional notes..."
                  value={newCatDesc}
                  onChange={(e) => setNewCatDesc(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowCategoryModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-medium rounded-xl text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="px-6 py-2 bg-primary text-white font-medium rounded-xl text-sm shadow-sm">Save Category</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Add Fee Structure Entry */}
      {showStructureModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="font-bold text-lg text-slate-800">Add Fee Structure Entry</h3>
              <button onClick={() => setShowStructureModal(false)} className="font-bold text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleAddStructure} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Class <span className="text-red-500">*</span></label>
                <select
                  value={targetClass}
                  onChange={(e) => setTargetClass(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                >
                  <option value="1">Class 1</option>
                  <option value="2">Class 2</option>
                  <option value="3">Class 3</option>
                  <option value="4">Class 4</option>
                  <option value="5">Class 5</option>
                  <option value="6">Class 6</option>
                  <option value="7">Class 7</option>
                  <option value="8">Class 8</option>
                  <option value="9">Class 9</option>
                  <option value="10">Class 10</option>
                  <option value="11">Class 11</option>
                  <option value="12">Class 12</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Category <span className="text-red-500">*</span></label>
                <select
                  value={targetCategory}
                  onChange={(e) => setTargetCategory(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Amount (Rs.) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 500.00"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Frequency</label>
                <select
                  value={targetFrequency}
                  onChange={(e) => setTargetFrequency(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one-time">One-time</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowStructureModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-medium rounded-xl text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="px-6 py-2 bg-primary text-white font-medium rounded-xl text-sm shadow-sm">Save Structure</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 3: Edit Fee Structure Entry */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="font-bold text-lg text-slate-800">Edit Fee Structure</h3>
              <button onClick={() => setShowEditModal(false)} className="font-bold text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleUpdateStructure} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Target Class</label>
                <select
                  value={editClass}
                  onChange={(e) => setEditClass(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map((c) => (
                    <option key={c} value={c}>Class {c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Fee Category</label>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Amount (Rs.) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 500.00"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase mb-2">Frequency</label>
                <select
                  value={editFrequency}
                  onChange={(e) => setEditFrequency(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one-time">One-time</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 font-medium rounded-xl text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="px-6 py-2 bg-primary text-white font-medium rounded-xl text-sm shadow-sm">{submitting ? 'Updating...' : 'Update Entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default FeeStructure;
